//@flow
import {
    territoryPlan as territoryApi,
    bulkTerritoryFetch as bulkTerritoryFetchApi,
    territoryStatusUpdate as territoryStatusUpdateApi,
    bulkDisableTerritory as bulkDisableTerritoryApi,
    disableTerritory as disableTerritoryApi,
    territory as automaticTerritoryCreateApi,
    manualTerritory as manualTerritoryCreateApi,
    territoryOutletMetrics,
    validateEntityIds as validateEntityIdsApi
} from 'common/middleware/api';
import PaginatedEntityActions from 'common/redux/actions/PaginatedEntityActions';
import { CreateTerritoryConfig } from 'common/models/flow/CreateTerritoryConfig';
import { type Dispatch } from 'redux';
import { showToast } from 'common/redux/actions/toast';
import { ToastTypes } from 'common/components/core/toast/Toast';
import I18nHelper from 'common/helpers/I18nHelper';

import { ENTITY_NAME_ID } from 'common/constants/entity/territoryMaster';
import { CreateTerritoryPlan } from 'common/models/internal/CreateTerritoryPlan';

const translationsNamespace = ['entityManagement'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);
type Params = {
    clientId: string,
    personnelId?: string
};

type OptionsType = {
    onSuccess?: (territoryPlan: CreateTerritoryPlan) => void,
    onFailure?: (error: Error) => void,
    ...
};

class TerritoryActions extends PaginatedEntityActions<
    CreateTerritoryPlan,
    Params
> {
    entityNameId = ENTITY_NAME_ID;
    automaticTerritoryCreateApi = automaticTerritoryCreateApi;
    manualTerritoryCreateApi = manualTerritoryCreateApi;
    territoryApi = territoryApi;
    outletMetricsApi = territoryOutletMetrics;
    static FETCH_EDITING_LAYER_START = 'FETCH_EDITING_LAYER_START';

    addAutomaticTerritoryPlan = (
        territoryConfig: CreateTerritoryConfig,
        options = {}
    ) => {
        return (dispatch: Dispatch<*>) => {
            const { onSuccess, onFailure } = options;
            return this.automaticTerritoryCreateApi
                .post(
                    {
                        clientId: territoryConfig.clientId
                    },
                    territoryConfig.toJS()
                )
                .then((response: any) => {
                    onSuccess && onSuccess(response);
                    return response;
                })
                .catch((error: Error) => {
                    onFailure && onFailure(error);
                    this.handleError(error, options, dispatch);
                });
        };
    };

    addManualTerritoryPlan = (
        territoryRequest: CreateTerritoryPlan,
        clientId: string,
        options: OptionsType = {}
    ) => {
        const { onSuccess, onFailure } = options;
        const territoryPlan = {
            clientId,
            name: territoryRequest.name,
            territoriesData: territoryRequest.territories
                .toJS()
                .map(territory => ({
                    name: territory.name,
                    outletsData: {
                        ids: territory.outletsInfo.outletIds.map(
                            ({ outletId }) => outletId
                        )
                    },
                    outerPolygon: territory.outerPolygon
                }))
        };

        return (dispatch: Dispatch<*>) => {
            return this.manualTerritoryCreateApi
                .post(
                    {
                        clientId
                    },
                    territoryPlan
                )
                .then((response: any) => {
                    onSuccess &&
                        onSuccess(CreateTerritoryPlan.fromJS(response));
                    return response;
                })
                .catch((error: Error) => {
                    onFailure && onFailure(error);
                    this.handleError(error, options, dispatch);
                });
        };
    };

    updateManualTerritoryPlan = (
        territoryRequest: CreateTerritoryPlan,
        clientId: string,
        options: OptionsType = {}
    ) => {
        const { onSuccess, onFailure } = options;
        return (dispatch: Dispatch<*>) => {
            return this.territoryApi
                .put(
                    {
                        clientId,
                        territoryId: territoryRequest.id
                    },
                    territoryRequest.toJS()
                )
                .then((response: any) => {
                    onSuccess &&
                        onSuccess(CreateTerritoryPlan.fromJS(response));
                    return response;
                })
                .catch((error: Error) => {
                    onFailure && onFailure(error);
                    this.handleError(error, options, dispatch);
                });
        };
    };

    fetchTerritory = (
        params: {
            territoryId: string,
            clientId: string
        },
        options: OptionsType
    ) => {
        return (dispatch: Dispatch<*>) => {
            const { onSuccess, onFailure } = options;
            return this.territoryApi
                .fetch(params)
                .then(entity => {
                    const plan = CreateTerritoryPlan.fromJS(entity);
                    onSuccess && onSuccess(plan);
                    return plan;
                })
                .catch((error: Error) => {
                    onFailure && onFailure(error);
                    this.handleError(error, options, dispatch);
                });
        };
    };

    superDisableEntity = this.disableEntity;

    disableEntity = (params: { clientId: string, locationId: string }) => {
        return (dispatch: Dispatch<*>) => {
            return new Promise((resolve, reject) => {
                const options = {
                    onSuccess: () => {
                        const toast = {
                            message: nsTranslate('deleteEntitySuccessMessage', {
                                entityNameId: this.entityNameId
                            }),
                            type: ToastTypes.Success
                        };
                        dispatch(showToast(toast, params));
                        resolve();
                    },
                    onFailure: (error: Error) => {
                        reject(error);
                    }
                };
                dispatch(this.superDisableEntity(params, options));
            });
        };
    };

    getOutletMetrics = ({
        clientId,
        ids
    }: {
        clientId: string,
        ids: Array<string>
    }) => {
        return this.outletMetricsApi
            .post({ clientId }, { ids })
            .then(response => response)
            .catch(error => {
                throw error;
            });
    };
}

export default new TerritoryActions(
    'TERRITORY',
    territoryApi,
    bulkTerritoryFetchApi,
    disableTerritoryApi,
    bulkDisableTerritoryApi,
    validateEntityIdsApi,
    null,
    territoryStatusUpdateApi,
    null
);
